import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useFormContext } from 'react-hook-form';
import NvSwitch from 'shared/components/inputs/nv-switch';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import NvTooltip, { TriggerType } from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { AdminUser } from 'redux/schemas/models/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import { getCourseAliases } from 'redux/selectors/course';
import { config } from '@config/pendo.config.json';
import { aiLearningSection, CommonHeaderSection, teachingTeam, tooltipStyles } from './styles';
import UserItem from './tag-component/tag-item';
import UserButtonField, {
  TagButtonType,
} from './tag-component/tag-button-field';
import AskNovoAIReadOnly from '../ask-novoai/components/ask-novoai-read-only';

interface AILearningAssistantProps {
  users: AdminUser[];
}

const MAX_TAGS = 5;

const AILearningAssistant = ({
  users: initialUsers = [],
}: AILearningAssistantProps) => {
  const { setValue, watch } = useFormContext();
  const isEnabled = watch('hasAiLearningAssistantEnabled');
  const { hasAiLearningAssistant } = useSelector(getCurrentInstitution);
  const aliases = useSelector(getCourseAliases);
  const currentUser = useSelector(getCurrentUser);

  const tooltipRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  // State to manage the selected recipients
  const [selectedTags, setSelectedTags] = useState<AdminUser[]>([]);
  // State to track available recipients that haven't been selected yet
  const [availableTags, setAvailableTags] = useState<AdminUser[]>(
    initialUsers || [],
  );

  const isDisabled = !hasAiLearningAssistant;

  // Handler to add a recipient
  const handleAdd = (id: number) => {
    const recipient = availableTags.find((r) => r.id === id);
    if (recipient) {
      const newTags = [...selectedTags, recipient];
      // Add to selected recipients
      setSelectedTags([...selectedTags, recipient]);
      // Remove from available recipients
      setAvailableTags(availableTags.filter((r) => r.id !== id));
      setValue('userCourseIds', newTags.map((r) => r.id), { shouldDirty: true });
    }
  };

  // Handler to remove a recipient
  const handleDeleteTag = (id: number) => {
    const recipient = selectedTags.find((r) => r.id === id);
    if (recipient) {
      const newTags = selectedTags.filter((r) => r.id !== id);
      // Remove from selected recipients
      setSelectedTags(newTags);
      // Add back to available recipients
      setAvailableTags([...availableTags, recipient]);
      setValue('userCourseIds', newTags.map((r) => r.id), { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (!initialUsers || initialUsers.length === 0) {
      return;
    }
    const initialSelectedTags = initialUsers?.filter(
      (user) => user.isQnaFacilitator,
    );
    setSelectedTags(initialSelectedTags);
    // Set available tags to those not selected
    setAvailableTags(
      initialUsers.filter((user) => !user.isQnaFacilitator),
    );
  }, [initialUsers]);

  const handleTooltipToggle = (isVisible: boolean) => {
    setShowTooltip(isVisible);
  };

  // Effect to add document click listener to close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close the tooltip if clicking outside the tooltip area
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    // Add event listener when tooltip is open
    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  const onChangeAiLearningAssistant = () => {
    if (isEnabled) {
      // Reset selected tags when enabling the AI assistant
      setSelectedTags([]);
      setAvailableTags(initialUsers || []);
      setValue('userCourseIds', [], { shouldDirty: true });
    }
    setValue('hasAiLearningAssistantEnabled', !isEnabled, { shouldDirty: true });
  };

  return (
    <div css={aiLearningSection(isDisabled)}>
      {/* HEADER */}
      <CommonHeaderSection disabled={isDisabled}>
        <div className='header-container'>
          <NvIcon
            icon='gen-ai'
            size='small'
            className={!isDisabled ? 'active' : 'disabled'}
          />
          <div className='title'>
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.TITLE()}
          </div>
        </div>
        {isDisabled && (
          <div className='alert-container'>
            <NvIcon icon='info' size='small' className='warning' />
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ALERT_AI_CAPABILITIES()}
          </div>
        )}
        <div className='description'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.DESCRIPTION(aliases?.courseAliases)}
        </div>

        <div className='switch-container'>
          <NvSwitch
            name='hasAiLearningAssistantEnabled'
            onChange={onChangeAiLearningAssistant}
            withForm
            enabledIcon='gen-ai'
            checked={isEnabled}
            dataQa={isEnabled ? config.pendo.aiLearning.enableSwitch : config.pendo.aiLearning.disableSwitch}
            className={`${isDisabled ? 'switch-disabled' : ''}`}
            disabled={isDisabled}
            ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.SWITCH_LABEL()}
          />
          <span className='switch-label' ref={tooltipRef}>
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.SWITCH_LABEL()}{' '}
            <NvTooltip
              className='custom-tooltip'
              css={tooltipStyles}
              placement='bottom'
              show={showTooltip}
              onToggle={handleTooltipToggle}
              triggerType={TriggerType.CLICK}
              text={<AskNovoAIReadOnly firstName={currentUser.firstName} />}
            >
              <span className='switch-highlight'>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.SEE_AN_EXAMPLE()}
              </span>
            </NvTooltip>
          </span>
        </div>
      </CommonHeaderSection>
      <div css={teachingTeam}>
        <p>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ANSWER_DESCRIPTION(aliases?.teachingTeamAliases)}
        </p>
        <div className='recipients-tags'>
          {selectedTags.map((recipient) => (
            <UserItem
              key={recipient.id}
              id={recipient.id}
              name={`${recipient.user.firstName} ${recipient.user.lastName}`}
              onDelete={handleDeleteTag}
            />
          ))}
          {(availableTags.length > 0 || selectedTags.length === MAX_TAGS) && (
            <UserButtonField
              type={TagButtonType.Normal}
              items={availableTags}
              selectedTags={selectedTags}
              disabled={isDisabled || selectedTags.length === MAX_TAGS || availableTags.length === 0}
              onAdd={handleAdd}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AILearningAssistant;
